import { createApiClient } from "../apiClientFactory";
import {
  StartLevelTestRequest,
  StartLevelTestResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  GetLevelTestStatusRequest,
  GetLevelTestStatusResponse,
  GetLevelTestResultRequest,
  GetLevelTestResultResponse,
  GetSessionSnapshotResponse,
  GetNextQuestionResponse,
} from "./level-test.interfaces";
import {
  setLevelTestSessionId,
  trackEvent,
} from "@/telemetry/appInsights";
import axios from "axios";

// Client HTTP configurado para level-tests
const apiClient = createApiClient('level-tests');

function updateLevelTestSessionId(sessionId?: string | null): void {
  if (sessionId) {
    setLevelTestSessionId(sessionId);
  }
}

function trackLevelTestApiError(operation: string, error: unknown): void {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  trackEvent("LevelTestApiError", {
    operation,
    ...(status != null ? { status: String(status) } : {}),
  });
}

/**
 * API real para teste de nivelamento
 * Endpoints: http://localhost:5259/api/level-tests/
 */

export const apiStartLevelTest = async (
  request: StartLevelTestRequest
): Promise<StartLevelTestResponse> => {
  try {
    const response = await apiClient.post<StartLevelTestResponse>(
      '/level-tests/start',
      request
    );
    updateLevelTestSessionId(response.data.sessionId);
    if (response.data.sessionId) {
      trackEvent("LevelTestSessionCreated", {
        sessionId: response.data.sessionId,
      });
    }
    return response.data;
  } catch (error) {
    trackLevelTestApiError("start", error);
    throw error;
  }
};

export const apiSubmitAnswer = async (
  request: SubmitAnswerRequest
): Promise<SubmitAnswerResponse> => {
  try {
    const response = await apiClient.post<SubmitAnswerResponse>(
      '/level-tests/submit-answer',
      request
    );
    return response.data;
  } catch (error) {
    trackLevelTestApiError("submit-answer", error);
    throw error;
  }
};

export const apiGetLevelTestStatus = async (
  request: GetLevelTestStatusRequest
): Promise<GetLevelTestStatusResponse> => {
  const response = await apiClient.get<GetLevelTestStatusResponse>(
    `/level-tests/status/${request.userId}`
  );
  return response.data;
};

export const apiGetLevelTestResult = async (
  request: GetLevelTestResultRequest
): Promise<GetLevelTestResultResponse> => {
  const response = await apiClient.get<GetLevelTestResultResponse>(
    `/level-tests/result/${request.sessionId}`
  );
  return response.data;
};

export const apiAbortLevelTest = async (sessionId: string): Promise<void> => {
  await apiClient.delete(`/level-tests/session/${sessionId}`);
};

export const apiSkipLevelTest = async (): Promise<{ sessionId?: string; status?: string }> => {
  const response = await apiClient.post<{ sessionId?: string; status?: string }>(
    "/level-tests/skip"
  );
  updateLevelTestSessionId(response.data.sessionId);
  return response.data;
};

export const apiGetSessionSnapshot = async (): Promise<GetSessionSnapshotResponse> => {
  console.log("🔍 [API] apiGetSessionSnapshot - Making HTTP call to /level-tests/session/snapshot");
  
  try {
    const response = await apiClient.get<GetSessionSnapshotResponse>(
      '/level-tests/session/snapshot'
    );
    
    updateLevelTestSessionId(response.data.sessionId);
    if (response.data.sessionId) {
      trackEvent("LevelTestSessionCreated", {
        sessionId: response.data.sessionId,
      });
    }

    console.log("🔍 [API] apiGetSessionSnapshot - Raw HTTP response:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataDetails: {
        sessionId: response.data.sessionId,
        answeredCount: response.data.answered?.length || 0,
        isTestCompleted: response.data.isTestCompleted,
        answeredQuestions: response.data.answered?.map(item => ({
          order: item.order,
          questionId: item.question?.id,
          questionText: item.question?.questionText,
          optionsCount: item.question?.options?.length || 0
        })) || []
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("🔍 [API] apiGetSessionSnapshot - HTTP Error:", error);
    trackLevelTestApiError("session-snapshot", error);
    throw error;
  }
};

export const apiGetNextQuestion = async (): Promise<GetNextQuestionResponse> => {
  console.log("🔍 [API] apiGetNextQuestion - Making HTTP call to /level-tests/next-question");
  
  try {
    const response = await apiClient.get<GetNextQuestionResponse>(
      '/level-tests/next-question'
    );
    
    updateLevelTestSessionId(response.data.sessionId);

    if (
      response.data.sessionId &&
      !response.data.isTestCompleted &&
      !response.data.question
    ) {
      trackEvent("LevelTestNextQuestionEmpty", {
        sessionId: response.data.sessionId,
      });
    }

    console.log("🔍 [API] apiGetNextQuestion - Raw HTTP response:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataDetails: {
        sessionId: response.data.sessionId,
        questionNumber: response.data.questionNumber,
        isTestCompleted: response.data.isTestCompleted,
        questionData: response.data.question ? {
          id: response.data.question.id,
          questionText: response.data.question.questionText,
          questionType: response.data.question.questionType,
          difficultyLevel: response.data.question.difficultyLevel,
          skillArea: response.data.question.skillArea,
          optionsCount: response.data.question.options?.length || 0,
          options: response.data.question.options?.map(opt => ({
            id: opt.id,
            text: opt.text,
            order: opt.order
          })) || []
        } : null
      }
    });
    
    return response.data;
  } catch (error) {
    console.error("🔍 [API] apiGetNextQuestion - HTTP Error:", error);
    trackLevelTestApiError("next-question", error);
    throw error;
  }
};
